import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import {
  $isTextNode,
  DOMConversion,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  DOMExportOutputMap,
  EditorState,
  isHTMLElement,
  Klass,
  LexicalEditor,
  LexicalNode,
  ParagraphNode,
  TextNode,
} from "lexical";
import { CSSProperties, useEffect } from "react";
import { useToken } from "../../hooks/useToken";
import { MyOnChangePlugin } from "./plugins/ChangePlugin";
import ToolbarPluginTeste from "./pluginsTeste/ToolbarPluginTeste";
import Theme from "./theme";

const placeholder = "Digite seu texto aqui...";

const removeStylesExportDOM = (
  editor: LexicalEditor,
  target: LexicalNode
): DOMExportOutput => {
  const output = target.exportDOM(editor);
  if (output && isHTMLElement(output.element)) {
    for (const el of [
      output.element,
      ...(output.element.querySelectorAll("[style],[class]") as any),
    ]) {
      el.removeAttribute("class");
      el.removeAttribute("style");
    }
  }
  return output;
};

const exportMap: DOMExportOutputMap = new Map<
  Klass<LexicalNode>,
  (editor: LexicalEditor, target: LexicalNode) => DOMExportOutput
>([
  [ParagraphNode, removeStylesExportDOM],
  [TextNode, removeStylesExportDOM],
  [HeadingNode, removeStylesExportDOM],
  [QuoteNode, removeStylesExportDOM],
  [ListNode, removeStylesExportDOM],
  [ListItemNode, removeStylesExportDOM],
  [LinkNode, removeStylesExportDOM],
  [CodeNode, removeStylesExportDOM],
]);

const getExtraStyles = (element: HTMLElement): string => {
  return "";
};

const constructImportMap = (): DOMConversionMap => {
  const importMap: DOMConversionMap = {};
  const nodeImporters = [
    TextNode.importDOM(),
    ParagraphNode.importDOM(),
    HeadingNode.importDOM(),
    QuoteNode.importDOM(),
    ListNode.importDOM(),
    ListItemNode.importDOM(),
    LinkNode.importDOM(),
    CodeNode.importDOM(),
  ];
  for (const importer of nodeImporters) {
    if (importer) {
      for (const [tag, fn] of Object.entries(importer)) {
        importMap[tag] = (
          node: HTMLElement
        ): DOMConversion<HTMLElement> | null => {
          const importerResult = fn(node);
          if (!importerResult || !importerResult.conversion) return null;
          const originalConversion = importerResult.conversion;
          return {
            ...importerResult,
            conversion: (element: HTMLElement): DOMConversionOutput | null => {
              const output = originalConversion(element);
              if (output && output.forChild && output.node === null) {
                const originalForChild = output.forChild;
                const adjustedForChild = (
                  lexicalNode: LexicalNode | null,
                  parent: LexicalNode | null | undefined
                ): LexicalNode | null => {
                  const possiblyModifiedNode = originalForChild(
                    lexicalNode as LexicalNode,
                    parent
                  );
                  if (!possiblyModifiedNode) return null;
                  const extraStyles = getExtraStyles(element);
                  if ($isTextNode(possiblyModifiedNode) && extraStyles) {
                    try {
                      const writableNode = possiblyModifiedNode.getWritable();
                      writableNode.setStyle(
                        writableNode.getStyle() + extraStyles
                      );
                      return writableNode;
                    } catch (e) {
                      console.warn("Could not set style on imported node", e);
                      return possiblyModifiedNode;
                    }
                  }
                  return possiblyModifiedNode;
                };
                return { ...output, forChild: adjustedForChild };
              }
              return output;
            },
          };
        };
      }
    }
  }
  return importMap;
};

function EditorStateSync({
  editorStateProp,
}: {
  editorStateProp: EditorState | string | undefined;
}) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    if (editorStateProp && editor) {
      let newState: EditorState;
      try {
        newState =
          typeof editorStateProp === "string"
            ? editor.parseEditorState(editorStateProp)
            : editorStateProp;
        const currentEditorStateJSON = JSON.stringify(
          editor.getEditorState().toJSON()
        );
        const newEditorStateJSON = JSON.stringify(newState.toJSON());
        if (currentEditorStateJSON !== newEditorStateJSON) {
          editor.setEditorState(newState);
        }
      } catch (error) {
        console.error("Error parsing or setting editor state:", error);
      }
    }
  }, [editorStateProp, editor]);
  return null;
}

interface EditorTesteProps {
  maxLength?: number;
  state: EditorState | string | undefined;
  onState: (data: EditorState) => void;
  extraStyle?: CSSProperties;
  styles?: {
    editorContainer?: CSSProperties;
    editorInner?: CSSProperties;
    editorInput?: CSSProperties;
  };
}

export function EditorTeste({
  state,
  onState,
  maxLength,
  extraStyle = {},
  styles = {},
}: EditorTesteProps) {
  const { token } = useToken();
  const placeholderJsx = (
    <div
      className="editor-placeholder"
      style={{
        color: token.colorTextPlaceholder,
        position: "absolute",
        top: "15px",
        left: "10px",
        pointerEvents: "none",
        userSelect: "none",
        zIndex: 0,
      }}
    >
      {placeholder}
    </div>
  );

  const initialConfig = {
    editable: true,
    namespace: "EditorTeste",
    theme: Theme,
    onError(error: Error) {
      console.error("Lexical Error:", error);
    },
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
      ParagraphNode,
      TextNode,
    ],
    html: { export: exportMap, import: constructImportMap() },
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <style>{`
           .editor-paragraph { margin: 0 0 8px 0; position: relative; color: ${
             extraStyle?.color || "inherit"
           };}
           .editor-textBold { font-weight: bold; }
           .editor-textItalic { font-style: italic; }
           .editor-textUnderline { text-decoration: underline; }
           .editor-textStrikethrough { text-decoration: line-through; }
           .editor-textCode { background-color: rgba(211, 211, 211, 0.3); padding: 1px 0.25rem; font-family: monospace; font-size: 94%; border-radius: 3px; }
           .editor-link { color: ${
             token.colorLink || "#1677ff"
           }; text-decoration: none; cursor: pointer; }
           .editor-link:hover { text-decoration: underline; }
           .editor-code { background-color: #f5f5f5; font-family: monospace; display: block; padding: 8px; margin: 8px 0; line-height: 1.4; font-size: 14px; overflow-x: auto; border-radius: 4px; }
           .editor-quote { margin: 0 0 8px 20px; border-left: 4px solid #eee; padding-left: 16px; color: #555; font-style: italic; }
           .editor-h1 { font-size: 24px; font-weight: bold; margin-bottom: 12px; padding-bottom: 0; }
           .editor-h2 { font-size: 20px; font-weight: bold; margin-bottom: 10px; padding-bottom: 0; }
           .editor-h3 { font-size: 16px; font-weight: bold; margin-bottom: 8px; padding-bottom: 0; }
           .editor-list-ol { margin: 0 0 8px 32px; padding-left: 0; list-style-type: decimal; }
           .editor-list-ul { margin: 0 0 8px 32px; padding-left: 0; list-style-type: disc; }
           .editor-listItem { margin: 4px 0; }
           .editor-nestedListItem { list-style-type: inherit; margin: 4px 0; }
           .editor-input:focus-visible { outline: none; }
           .editor-input { position: relative; }
         `}</style>

      <div
        className="editor-container"
        style={{
          maxWidth: "100%",
          color: token.colorText,
          position: "relative",
          lineHeight: "20px",
          fontWeight: 400,
          textAlign: "left",
          border: "none",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          ...styles?.editorContainer,
        }}
      >
        <ToolbarPluginTeste />

        <div
          className="editor-inner"
          style={{
            position: "relative",
            background: token.colorBgContainer,
            flexGrow: 1,
            overflowY: "auto",
            overflowX: "hidden",
            ...styles?.editorInner,
          }}
        >
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="editor-input"
                style={{
                  minHeight: "150px",
                  height: "100%",
                  resize: "none",
                  fontSize: "15px",
                  position: "relative",
                  tabSize: 4,
                  outline: "none",
                  padding: "15px 10px",
                  caretColor: token.colorPrimary,
                  cursor: "text",
                  ...styles?.editorInput,
                }}
              />
            }
            placeholder={placeholderJsx}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <EditorStateSync editorStateProp={state} />
          <MyOnChangePlugin onChange={onState} />
          <HistoryPlugin />
          <AutoFocusPlugin />
          <ListPlugin />
          <LinkPlugin />
          <TabIndentationPlugin />
          {/* <MarkdownShortcutPlugin transformers={TRANSFORMERS} /> */}
        </div>
      </div>
    </LexicalComposer>
  );
}
